<template>
  <div
    class="airlst-app-container bg-white bg-cover bg-no-repeat relative flex flex-col ds:bg-canvas"
    :class="activeCompany && onFreePlan && 'is-free'"
    :style="
      !designSystem && {
        'background-image': `url('${Vapor.asset('images/airlst-background-2025.jpg')}')`,
        'background-repeat-y': 'no-repeat',
      }
    "
  >
    <a
      href="#main-content"
      class="bypass-blocks-button bg-primary py-2 px-3 absolute text-white z-100 font-medium text-[11px] md:text-sm outline-offset-[3px]"
      >{{ $t('wcag.skip_to_main_content') }}</a
    >

    <div class="sticky top-0 z-30">
      <FreePlanBanner v-if="activeCompany && onFreePlan" />
      <Header v-if="!designSystem" />
    </div>
    <div class="relative flex overflow-x-clip flex-1">
      <AppSidebar
        v-if="designSystem"
        :account-in-foot="accountInFoot"
        @toggle-sidebar="toggleSidebar"
      />
      <SectionNav
        v-if="showSectionTabs && !sectionNavCollapsed"
        :key="currentSection.name"
      />
      <!-- Pages outside the event sections (the user settings) mount their own column here. -->
      <div v-if="designSystem" id="section-nav-slot" class="contents" />
      <EventSidebar
        v-if="pageHaveEvent && !designSystem"
        :collapsed="isSidebarCollapsed"
        @toggle-sidebar="toggleSidebar"
      />
      <!-- Scroll architecture (AIRLST-5621). Two rules, each independent of the
           other so neither is load-bearing for the other:

           1. This shell owns the vertical scroll and is `h-body` tall — exactly
              the viewport below the header. <main> deliberately keeps its
              automatic min-height, so it can never shrink below its own content
              and the footer is laid out *after* that content, at the end of the
              document. `min-h-0` collapses <main> to the leftover space instead,
              which pins the footer to the viewport bottom and lets the content
              spill past it — that was the AIRLST-5604 regression — so it stays
              scoped to the Airstage branch alongside its siblings.
           2. `min-w-0` is set explicitly rather than inherited from the overflow
              branch. An overflow value other than `visible` forces a flex item's
              automatic min-width to 0 as a side effect; relying on that made the
              horizontal behaviour depend on which overflow branch was active and
              crushed the event sidebar when it changed (AIRLST-5608). -->
      <!-- The page scrolls in here, not on <body>, so Inertia cannot reset the
           position on a visit unless the container is marked as a scroll region.
           Without it every navigation keeps the previous page's offset, and the
           47 deliberate `preserveScroll` call sites have nothing to preserve
           against. -->
      <div
        scroll-region
        class="relative flex flex-col flex-1 min-w-0 h-body"
        :class="
          isAirstageCanvas
            ? 'overflow-hidden'
            : 'overflow-y-auto overflow-x-hidden'
        "
      >
        <main
          id="main-content"
          class="relative flex-1"
          :class="
            isAirstageCanvas
              ? 'flex flex-col overflow-hidden min-h-0'
              : 'app-main'
          "
          :style="sectionTabsStyle"
          :data-event-app-tabs="showSectionTabs ? '' : undefined"
          :data-section-head="
            showSectionTabs && !showSectionHead ? 'none' : undefined
          "
          :data-ds-page-header="showPageHeader ? '' : undefined"
        >
          <SectionTabs v-if="showSectionTabs && showSectionHead" />
          <PageHeader v-else-if="showPageHeader && !showSectionTabs" />
          <!-- A page the trail reaches in a single step has no headline row: the bell and the
               user menu float top right, on the line a headline row would have put them on. The
               full-screen tools (App Builder, Airstage canvas) keep them in the sidebar foot
               instead, since floating there they draw over the tool's own panel header. -->
          <div
            v-else-if="designSystem && !accountInFoot"
            class="absolute top-[17px] right-4 z-20 flex justify-end lg:right-[28px]"
            data-test="floating-header-actions"
          >
            <HeaderActions />
          </div>
          <slot />
          <PlannerPageSkeleton />
        </main>
        <Footer v-if="!isAirstageCanvas" />
      </div>
    </div>

    <Chat v-if="enableChat" />

    <!-- „So funktioniert's“: one video modal for every planner page (row, pill, start page, ?video=1). -->
    <PlannerVideoModal v-if="showPlannerVideo" />

    <!-- Design-system look: messages are toasts at the bottom centre (AppToaster); the bell's
         live notifications arrive there too, through the same Notifications listener. -->
    <template v-if="designSystem">
      <!-- Outside #app: an open dialog makes #app inert (Headless UI), which would leave the toasts
           unclickable and unannounced while it is open. -->
      <Teleport to="body">
        <AppToaster />
      </Teleport>
      <!-- The headless listener renders its default slot; it has nothing to draw here. -->
      <Notifications @new-notification="onNewNotification">
        <template #default />
      </Notifications>
    </template>

    <div
      v-else
      class="fixed top-20 right-5 z-99 space-y-2"
      data-test="notifications-container"
    >
      <Notifications @new-notification="onNewNotification">
        <transition
          enter-active-class="transform ease-out duration-300 transition"
          enter-from-class="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
          enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
          leave-active-class="transition ease-in duration-100"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <NewNotification
            v-if="showNewNotification"
            :notification="newNotificationContent"
            @dismissed="dismissNewNotification"
          />
        </transition>
      </Notifications>

      <NotificationsList :notification="bannerNotification" />
    </div>

    <SubscriptionAlert />
    <DesignSystemInvitation />
    <InertiaTimeoutModal />
  </div>
</template>

<script setup>
import { useDesignSystem } from '@/composables/designSystem'
import Footer from '@/Components/Footer.vue'
import Header from '@/Components/Header.vue'
import FreePlanBanner from '@/Components/FreePlanBanner.vue'
import DesignSystemInvitation from '@/Components/DesignSystemInvitation.vue'
import Chat from '@/Components/Headless/Chat'
import SubscriptionAlert from '@/Components/SubscriptionAlert.vue'
import InertiaTimeoutModal from '@/Components/InertiaTimeoutModal.vue'
import Vapor from 'laravel-vapor'
import { useFlash } from '@/composables/useFlash'
import { hasChatAccess } from '@/composables/subscription'
import { usePage } from '@inertiajs/vue3'
import NewNotification from '@/Components/Notifications/NewNotification.vue'
import NotificationsList from '@/Components/Notifications/NotificationsList.vue'
import AppToaster from '@/Components/Notifications/AppToaster.vue'
import { notify } from '@/composables/toast'
import { LINK_PLACEHOLDER } from '@/composables/notification'
import { ref, computed, provide, onMounted, onBeforeUnmount, watch } from 'vue'
import Notifications from '@/Components/Headless/Notifications'
import EventSidebar from '@/Components/Sidebar/EventSidebar.vue'
import AppSidebar from '@/Components/Sidebar/AppSidebar.vue'
import HeaderActions from '@/Components/Sidebar/HeaderActions.vue'
import PageHeader from '@/Components/Sidebar/PageHeader.vue'
import { useBreadcrumbs } from '@/composables/breadcrumbs'
import SectionNav from '@/Components/Sidebar/SectionNav.vue'
import SectionTabs from '@/Components/Sidebar/SectionTabs.vue'
import PlannerPageSkeleton from '@/Components/Planner/PlannerPageSkeleton.vue'
import PlannerVideoModal from '@/Components/Planner/PlannerVideoModal.vue'
import { isPlannerComponent } from '@/composables/plannerSkeleton'
import { usePlannerVideoAvailable } from '@/composables/plannerVideo'
import {
  sectionNavCollapsed,
  useCurrentSectionMenu,
} from '@/composables/sectionTabs'

const activeCompany = usePage().props._user.active_company

/*
 * Parsed once per flash, not on every render: the layout re-renders on its own
 * (e.g. the notification polling), and a freshly parsed object would show the
 * same banner again each time.
 */
const bannerNotification = computed(() => useFlash('bannerNotification'))

// The design-system look has no top header: its functions live in the icon rail.
const designSystem = useDesignSystem()

/**
 * Design-system look: every page of a sidebar section with a submenu (guests, content, event app,
 * tickets …, and the organisation level's content outside an event) shows the section's pages in a column beside the page (SectionNav; pill tabs on
 * narrow screens) and a headline (SectionTabs) instead of the sidebar flyout; the App Builder
 * keeps the column but drops the headline (showSectionHead). Page wrappers with
 * a fixed height subtract `--event-app-tabs`; toolkit screens (EventAppEmbed, absolutely
 * positioned over the main area) start below `--event-app-tabs-offset`.
 */
const currentSection = computed(() =>
  designSystem.value ? useCurrentSectionMenu() : null,
)

/**
 * The headline row with the breadcrumb trail on every page of the design-system look but the
 * Airstage canvas: SectionTabs on section pages, PageHeader elsewhere. While it shows, the
 * pages' own titles are hidden (theme.css, [data-ds-page-header]).
 */
const showBreadcrumbs = computed(
  () => designSystem.value && !isAirstageCanvas.value,
)

const breadcrumbs = useBreadcrumbs()

const showPageHeader = computed(
  () => showBreadcrumbs.value && breadcrumbs.value.length > 1,
)

const showSectionTabs = computed(() => currentSection.value !== null)

const plannerVideoAvailable = usePlannerVideoAvailable()
const showPlannerVideo = computed(
  () => plannerVideoAvailable.value && isPlannerComponent(usePage().component),
)

/**
 * The App Builder is a full-screen tool: it keeps the section column (and folding it away), but
 * drops the headline with the breadcrumbs above it so the whole builder fits on the screen; it
 * brings its own preview buttons. theme.css ([data-section-head='none']) removes the headline's
 * height from the offsets.
 */
const showSectionHead = computed(
  () => usePage().component !== 'EventApp/AppBuilder',
)

/**
 * The full-screen tools: no headline row, and their own panels reach the top right corner, so the
 * bell and the user menu sit in the sidebar foot rather than floating over them.
 */
const accountInFoot = computed(
  () =>
    designSystem.value &&
    (isAirstageCanvas.value ||
      (showSectionTabs.value && !showSectionHead.value)),
)

/**
 * The event app's embedded pages run to 25px above the bottom edge instead of the page grid's
 * 64px.
 */
const sectionTabsStyle = computed(() => {
  if (!showSectionTabs.value) {
    return undefined
  }
  // The heights above the page (--event-app-tabs, --event-app-tabs-offset) depend on the screen
  // width (pill row below lg, section column from lg up), so they live in theme.css.
  const style = {}
  if (currentSection.value.name === 'EventApp') {
    style['--event-app-bottom'] = '25px'
    style.paddingBottom = '25px'
  }
  return Object.keys(style).length ? style : undefined
})

// chat enabled for free and premium companies
const enableChat = hasChatAccess() || !activeCompany?.subscribed

const onFreePlan = activeCompany?.subscription_plan === 'free'

const newNotificationContent = ref({})
let newNotificationTimeout = null

const isSidebarCollapsed = ref(false)
provide('isSidebarCollapsed', isSidebarCollapsed)

const pageHaveEvent = computed(() => usePage().props.event)
const isAirstageCanvas = computed(() => {
  const component = usePage().component
  return (
    component === 'Airstage/Editor' ||
    component === 'Airstage/Builder' ||
    component === 'Airstage/ModuleBuilder'
  )
})
const showNewNotification = computed(
  () => Object.keys(newNotificationContent.value).length > 0,
)
const onNewNotification = (notification) => {
  if (designSystem.value) {
    notifyLiveNotification(notification)
    return
  }
  clearTimeout(newNotificationTimeout)
  newNotificationContent.value = notification
  newNotificationTimeout = setTimeout(
    () => (newNotificationContent.value = {}),
    5000,
  )
}
const dismissNewNotification = () => {
  clearTimeout(newNotificationTimeout)
  newNotificationContent.value = {}
}

/**
 * Design-system look: a live notification from the bell (an import finished, an export is ready)
 * is an info toast with its link as the action; the bell keeps it in its history.
 */
const notifyLiveNotification = (notification) => {
  const link = notification.link ?? null
  const title = (notification.title ?? '').replace(
    LINK_PLACEHOLDER,
    link?.text ?? '',
  )
  notify({
    type: 'info',
    headline: title,
    message: notification.description ?? null,
    action: link?.href
      ? { label: link.text, href: link.href, target: link.target }
      : null,
  })
}

/**
 * Design-system look: a backend flash message (bannerNotification, or setFlash on the client)
 * becomes a toast. It is taken out of the shared `_flash` prop once shown, so a partial reload
 * that keeps the prop, or a re-render, never raises it twice.
 */
const showFlashToast = (rawFlash) => {
  if (!designSystem.value) {
    return
  }
  let flash
  try {
    flash = JSON.parse(rawFlash ?? '{}')
  } catch {
    return
  }
  const banner = flash?.payload?.bannerNotification
  if (!banner) {
    return
  }
  notify(banner)
  delete flash.payload.bannerNotification
  usePage().props._flash = JSON.stringify(flash)
}

watch(() => usePage().props._flash, showFlashToast, { immediate: true })

// Embedded portable tools (e.g. the suite-toolkit web component) don't draw
// their own toasts - they dispatch a composed `eab:notify` DOM event that bubbles
// to the window. Route it through the Suite's own notification UI so it looks and
// stacks exactly like every other notification, not a bespoke toast. The
// design-system look takes an optional `type` (success, info, error; info when
// absent).
const onEabNotify = (event) => {
  const message = event?.detail?.message
  if (typeof message !== 'string' || message.length === 0) {
    return
  }
  if (designSystem.value) {
    notify({ type: event.detail.type ?? 'info', message })
    return
  }
  onNewNotification({ title: message })
}

onMounted(() => {
  let checkIfCollapsed = localStorage.getItem('sidebar-collapsed')
  isSidebarCollapsed.value = checkIfCollapsed === 'true'

  window.addEventListener('eab:notify', onEabNotify)
})

onBeforeUnmount(() => {
  window.removeEventListener('eab:notify', onEabNotify)
})

const toggleSidebar = (collapse) => {
  isSidebarCollapsed.value = collapse
  localStorage.setItem('sidebar-collapsed', collapse)
}
// setup usetiful
window.usetifulTags = {
  userId: usePage().props._user.id,
  firstName: usePage().props._user.first_name,
  lastName: usePage().props._user.last_name,
}
const script = document.createElement('script')
script.async = 1
script.src = 'https://www.usetiful.com/dist/usetiful.js'
script.setAttribute('id', 'usetifulScript')
script.dataset.token = '625e18d3be338183444b818f9fed5041'
document.body.appendChild(script)
</script>
